import { create } from 'zustand';
import type { FsSimpleData } from '@/services/auditService/auditAdjustmentService';
import {
  getWorkingPaper,
  type WorkingPaperQueryRequest,
} from '@/services/auditService/auditWorkingPaperService';
import { 
  queryAdjustmentList,
  draftFinalAmount,
  type AuditAdjustRecordList,
  type AuditAdjust,
  type FsNodeDraft,
} from '@/services/auditService/auditAdjustOrchestrate';
import userStore from '@/store/admin/userStore';
import { FS_CODE, findNodeByCodes, isCode } from '@/utils/fsCodes';
import type { RoundingAdjustGroupVO } from '@/services/auditService/roundingAdjustService';
import { applyAuditedRoundToDraftNodes } from '@/pages/components/fs-report/roundedDraftCompute';
import { resolvePlLeafAuditedValue } from '@/pages/components/fs-report/FsFormula';
import { resolveWpFetchCode } from '@/adjustment/adjust/dialogs/utils/wpSpecialSyncCodes';

/** In-flight draftFinalAmount by companyId:annualId — coalesce parallel callers. */
const draftFinalAmountInFlight = new Map<string, Promise<boolean>>();

/**
 * Convert AuditFsItemVO to FsSimpleData format for backward compatibility
 */
function convertAuditFsItemVOToFsSimpleData(vo: any): FsSimpleData {
  const level = vo.level || 0;
  
  // Recursively convert children if they exist
  const children = vo.children && Array.isArray(vo.children) 
    ? vo.children.map(convertAuditFsItemVOToFsSimpleData)
    : [];
  
  return {
    fsId: vo.fsId,
    level: level,
    name: vo.name || '',
    nameEn: vo.nameEn ?? null,
    nameZh: vo.nameZh ?? null,
    aliasEn: vo.aliasEn ?? null,
    aliasZh: vo.aliasZh ?? null,
    aliasLocked: vo.aliasLocked ?? null,
    statement: vo.statement ?? null,
    code: vo.code || '',
    entry: (vo.entry || '') as 'Dr' | 'Cr',
    scheduleName: vo.scheduleName || '',
    scheduleCode: vo.scheduleCode || '',
    openingAmount: vo.openingAmount ?? vo.client ?? vo.amount ?? 0,
    amountsByYearOfAssessment: vo.amountsByYearOfAssessment || null,
    adjustment: (Array.isArray(vo.adjustment) ? vo.adjustment : vo.adjustByPapers)?.map((paper: any) => ({
      refNo: paper.refNo || '',
      adjustment: paper.adjustment || 0,
      headerId: paper.headerId,
      adjustmentMode: paper.adjustmentMode,
      isAutoAdj: paper.isAutoAdj ?? (paper.adjustmentMode === 4),
      autoAdjType: paper.autoAdjType,
    })) || [],
    finalAmount: vo.audited || 0,
    lastYearAudited: vo.lastYearChangeAudited ?? vo.lastYearAudited ?? 0,
    lastYearChangeAudited: vo.lastYearChangeAudited ?? null,
    isCustom: vo.isCustom ?? false,
    customNodeId: vo.customNodeId ?? null,
    templateCodeRef: vo.templateCodeRef ?? null,
    adjustByPaper: vo.adjustByPaper || false,
    wpStatus: vo.wpStatus ?? null,
    wpIssueRemark: vo.wpIssueRemark ?? null,
    outstanding: vo.outstanding ?? false,
    outstandingRemark: vo.outstandingRemark ?? null,
    issueStatus: vo.issueStatus ?? null,
    notes: vo.notes ?? null,
    children: children // Preserve existing tree structure
  };
}

/**
 * Convert AuditAdjustRecordListVO to old AdjustRecord format.
 * When yearOfAssessment is set (MULTI), prefer that period's plLine over engagement Σ.
 */
function convertToLegacyFormat(data: AuditAdjustRecordList, yearOfAssessment?: string | null): any {
  const bsLine = (data.bsLine || []).map(convertAuditFsItemVOToFsSimpleData);
  const yoa = (yearOfAssessment ?? '').trim();
  let plSource = data.plLine || [];
  if (yoa && Array.isArray(data.periods) && data.periods.length > 0) {
    const hit = data.periods.find(
      (p) => (p?.yearOfAssessment || '').trim().toLowerCase() === yoa.toLowerCase()
        && Array.isArray(p.plLine)
        && p.plLine.length > 0
    );
    if (hit?.plLine) {
      plSource = hit.plLine;
    }
  }
  const plLine = plSource.map(convertAuditFsItemVOToFsSimpleData);

  return {
    companyId: data.companyId,
    annualId: data.annualId,
    inCorpDate: data.inCorpDate || '',
    bsLine: bsLine,
    plLine: plLine
  };
}

/**
 * 将树节点转换为 FsNodeDraft 格式，用于保存到 Redis
 * 只保留 code 和 finalAmount 关键字段
 * 叶子节点: 与 ProfitLoss Audited 同口径（resolvePlLeafAuditedValue）
 * 父节点(level2): finalAmount = sum of children's finalAmount
 * COT / 派生节点最终值由 applyAuditedRoundToDraftNodes 覆盖
 */
function convertToFsNodeDraft(node: any, sumAdjustments: (n: any) => number): FsNodeDraft {
  const code = String(node?.code || node?.scheduleCode || '').trim();
  
  const children = Array.isArray(node?.children) && node.children.length > 0
    ? node.children.map((child: any) => convertToFsNodeDraft(child, sumAdjustments))
    : undefined;
  
  let finalAmount: number;
  if (children && children.length > 0) {
    finalAmount = children.reduce((sum: number, child: FsNodeDraft) => sum + child.finalAmount, 0);
  } else {
    // WHY: 与页面 Audited 对齐；PL-AE-ADM+adjustByPaper 取 finalAmount（接口 audited）
    const fsId = Number(node?.fsId);
    const opening = Number(node?.openingAmount ?? node?.amount ?? 0) || 0;
    const withAdj = opening + sumAdjustments(node);
    const clientMap = Number.isFinite(fsId) ? {[fsId]: opening} : {};
    const auditedMap = Number.isFinite(fsId) ? {[fsId]: withAdj} : {};
    finalAmount = resolvePlLeafAuditedValue(node, clientMap, auditedMap);
  }
  
  return {
    code,
    finalAmount,
    roundingAdjustment: 0,
    children,
  };
}

/**
 * 将树数组转换为 FsNodeDraft 数组
 */
function convertTreeToFsNodeDrafts(trees: any[], sumAdjustments: (n: any) => number): FsNodeDraft[] {
  return (trees || []).map(node => convertToFsNodeDraft(node, sumAdjustments));
}

function createSumAdjustments() {
  return (node: FsSimpleData): number => {
    if (!Array.isArray((node as any)?.adjustment)) return 0;
    return (node as any).adjustment.reduce((sum: number, adj: any) => sum + (Number(adj?.adjustment) || 0), 0);
  };
}

function buildComputedDraftNodes(
  record: AuditAdjustRecordList,
  roundingGroup?: RoundingAdjustGroupVO | null,
  yearOfAssessment?: string | null
): { bsNodes: FsNodeDraft[]; plNodes: FsNodeDraft[] } {
  const legacyData = convertToLegacyFormat(record, yearOfAssessment);
  const sumAdjustments = createSumAdjustments();

  const bsNodes = convertTreeToFsNodeDrafts(legacyData.bsLine || [], sumAdjustments);
  const plNodes = convertTreeToFsNodeDrafts(legacyData.plLine || [], sumAdjustments);

  applyAuditedRoundToDraftNodes(
    legacyData.bsLine || [],
    legacyData.plLine || [],
    bsNodes,
    plNodes,
    roundingGroup,
    yearOfAssessment,
  );

  return { bsNodes, plNodes };
}

export interface FsAdjustLine {
  fsId: number;
  level: number;
  name: string;
  code: string;  // 添加 code 字段，与 FsSimpleData 保持一致
  scheduleName: string;
  scheduleCode: string;
  openingAmount: number;
  finalAmount: number;
  adjustment: any[];
  // Optional fields that may exist from API
  [k: string]: any;
}

export type FsStatement = 'BS' | 'PL';

export type FsPerData = {
  auditedValue: number;
  clientValue?: number;
  lastYearAudited: number;
  statement: FsStatement;
};

 export type FsCommitNode = {
  fsId: number;
  auditedValue: number;
  children?: FsCommitNode[];
 };

 export type FsCommitData = {
  companyId: number;
  annualId: number;
  bsLine: FsCommitNode;
  plLine: FsCommitNode;
 };

interface FsAdjustState {
  // Store complete AuditAdjustRecordListVO (includes BS/PL trees)
  adjustRecord: AuditAdjustRecordList | null;

  // key: scheduleCode -> FsSimpleData (fast index for leaf nodes)
  byCode: Record<string, FsSimpleData>;

  // key: code -> computed per-node values
  perDataByCode: Record<string, FsPerData>;
  
  // Cache for paper details: scheduleCode -> details array
  wpDetailsCache: Record<string, any[]>;

  /** Rounding adjustment 数据（由 FsAdjustment 同步，用于 draftFinalAmount 计算） */
  roundingGroup: RoundingAdjustGroupVO | null;
  
  loading: boolean;
  error: string | null;

  syncDraftFinalAmount: (yearOfAssessment?: string | null) => Promise<boolean>;
  setRoundingGroup: (group: RoundingAdjustGroupVO | null) => void;
  syncFromQueryData: (data: AuditAdjustRecordList | null) => void;
  initialize: (companyId: number, annualId: number, status?: number[]) => Promise<void>;
  getLineByCode: (code: string) => FsSimpleData | null;
  getPerData: (code: string, yearOfAssessment?: string | null) => FsPerData | null;
  getBsTree: () => FsSimpleData[] | null;
  getPlTree: () => FsSimpleData[] | null;
  getCommitData: () => FsCommitData | null;
  getComputedDraftNodes: (yearOfAssessment?: string | null) => { bsNodes: FsNodeDraft[]; plNodes: FsNodeDraft[] } | null;
  getWpDetail: (
    companyId: number,
    annualId: number,
    code: string,
    options?: {yearOfAssessment?: string | null},
  ) => Promise<any[]>;
  clearWpCache: (code: string) => void;
  setComputedPerData: (code: string, data: FsPerData, yearOfAssessment?: string | null) => void;
  clear: () => void;
  saveDraftToBackend: () => Promise<boolean>;
  /** Patch item display name on BS/PL trees after alias edit */
  patchItemAlias: (code: string, patch: {
    name?: string | null;
    aliasEn?: string | null;
    aliasZh?: string | null;
  }) => void;
}

export const useFsAdjustStore = create<FsAdjustState>((set, get) => ({
  adjustRecord: null,
  byCode: {},
  perDataByCode: {},
  wpDetailsCache: {},
  roundingGroup: null,
  loading: false,
  error: null,

  getCommitData: () => {
    const record = get().adjustRecord;
    if (!record) return null;

    const sumAdjustments = (node: { adjustment?: any[] }): number => {
      if (!Array.isArray(node?.adjustment)) return 0;
      return (node.adjustment || []).reduce((sum: number, adj: any) => sum + (Number(adj?.adjustment) || 0), 0);
    };

    const toCommitNode = (vo: any): FsCommitNode => {
      const node = convertAuditFsItemVOToFsSimpleData(vo);
      const opening = Number((node as any)?.openingAmount ?? 0) || 0;
      const auditedValue = opening + sumAdjustments(node as any);
      const children = Array.isArray(vo?.children) ? vo.children.map(toCommitNode) : [];
      return {
        fsId: Number((node as any)?.fsId ?? 0) || 0,
        auditedValue,
        children,
      };
    };

    const wrapRoots = (roots: FsCommitNode[]): FsCommitNode => {
      if (roots.length === 1) return roots[0];
      const auditedValue = roots.reduce((sum, n) => sum + (Number(n?.auditedValue) || 0), 0);
      return { fsId: 0, auditedValue, children: roots };
    };

    const bsRoots = (record.bsLine || []).map(toCommitNode);
    const plRoots = (record.plLine || []).map(toCommitNode);

    return {
      companyId: Number((record as any)?.companyId ?? 0) || 0,
      annualId: Number((record as any)?.annualId ?? 0) || 0,
      bsLine: wrapRoots(bsRoots),
      plLine: wrapRoots(plRoots),
    };
  },

  /**
   * 清除特定 scheduleCode 的 paperDetail 缓存
   * @param scheduleCode schedule code
   */
  clearWpCache: (code: string) => {
    set((state) => {
      const newCache = {...state.wpDetailsCache};
      const prefix = `${code}@`;
      for (const key of Object.keys(newCache)) {
        if (key === code || key.startsWith(prefix)) {
          delete newCache[key];
        }
      }
      return {wpDetailsCache: newCache};
    });
  },

  clear: () => set({ 
    adjustRecord: null,
    byCode: {}, 
    perDataByCode: {},
    wpDetailsCache: {},
    roundingGroup: null,
    loading: false,
    error: null,
  }),

  /**
   * 初始化 store，调用 queryAdjustmentList 接口获取数据
   * @param companyId 公司 ID
   * @param annualId 年度 ID
   * @param status 状态数组，默认为 [0, 1]
   */
  initialize: async (companyId: number, annualId: number, status: number[] = [0, 1]) => {
    set({ loading: true, error: null });
    
    try {
      const userId = userStore.getCurrentUserId();
      
      // 调用 queryAdjustmentList 接口
      const result = await queryAdjustmentList({
        companyId,
        annualId,
        userId,
        status: status as any,
      });
      
      if (!result) {
        set({ 
          adjustRecord: null,
          byCode: {},
          loading: false,
          error: null,
        });
        return;
      }
      
      // Convert to legacy format for backward compatibility
      const legacyData = convertToLegacyFormat(result);
      
      // 构建 scheduleCode 索引
      const index: Record<string, FsSimpleData> = {};

      const perDataIndex: Record<string, FsPerData> = {};

      const sumAdjustments = (node: FsSimpleData): number => {
        if (!Array.isArray((node as any)?.adjustment)) return 0;
        return (node as any).adjustment.reduce((sum: number, adj: any) => sum + (Number(adj?.adjustment) || 0), 0);
      };
      
      const traverse = (node: FsSimpleData, statement: FsStatement) => {
        if (!node) return;
        const sc = String(node?.scheduleCode ?? '').trim();
        const code = String((node as any)?.code ?? '').trim();
        const normalizedCode = code ? code.toUpperCase() : '';
        const children = Array.isArray(node?.children) ? node.children : [];

        if (normalizedCode) {
          const opening = Number((node as any)?.openingAmount ?? (node as any)?.amount ?? 0) || 0;
          const auditedValue = opening + sumAdjustments(node);
          perDataIndex[normalizedCode] = {
            auditedValue,
            clientValue: opening,
            lastYearAudited: Number((node as any)?.lastYearAudited ?? 0) || 0,
            statement,
          };
        }
        
        // 索引所有带 code 的叶子节点（PL-TAX 等为 level 2，不能仅限 level 3）
        if (children.length === 0 && (code || sc)) {
          if (sc) index[sc] = node;
          if (code) index[code] = node;
        }
        
        // 递归遍历子节点
        for (const child of children) {
          traverse(child, statement);
        }
      };
      
      for (const node of (legacyData.bsLine || [])) {
        traverse(node, 'BS');
      }

      for (const node of (legacyData.plLine || [])) {
        traverse(node, 'PL');
      }
      
      set({ 
        adjustRecord: result,
        byCode: index,
        perDataByCode: perDataIndex,
        loading: false,
        error: null,
      });
    } catch (e: any) {
      set({ 
        loading: false, 
        error: e?.message || 'Failed to initialize adjustment data' 
      });
      throw e;
    }
  },

  /**
   * 从 React Query 数据同步到 store
   * 用于方案 A: React Query + fsAdjustStore 同步
   * @param data AuditAdjustRecordListVO 或 null
   */
  syncFromQueryData: (data: AuditAdjustRecordList | null) => {
    if (!data) {
      set({ 
        adjustRecord: null,
        byCode: {},
        perDataByCode: {},
      });
      return;
    }

    // Convert to legacy format for byCode / perData (single set = atomic for BS/PL consumers)
    const legacyData = convertToLegacyFormat(data);
    const index: Record<string, FsSimpleData> = {};
    const perDataIndex: Record<string, FsPerData> = {};

    const sumAdjustments = (node: FsSimpleData): number => {
      if (!Array.isArray((node as any)?.adjustment)) return 0;
      return (node as any).adjustment.reduce((sum: number, adj: any) => sum + (Number(adj?.adjustment) || 0), 0);
    };
    
    const traverse = (node: FsSimpleData, statement: FsStatement) => {
      if (!node) return;
      const sc = String(node?.scheduleCode ?? '').trim();
      const code = String((node as any)?.code ?? '').trim();
      const normalizedCode = code ? code.toUpperCase() : '';
      const children = Array.isArray(node?.children) ? node.children : [];

      if (normalizedCode) {
        const opening = Number((node as any)?.openingAmount ?? (node as any)?.amount ?? 0) || 0;
        const auditedValue = opening + sumAdjustments(node);
        perDataIndex[normalizedCode] = {
          auditedValue,
          clientValue: opening,
          lastYearAudited: Number((node as any)?.lastYearAudited ?? 0) || 0,
          statement,
        };
      }
      
      // 索引所有带 code 的叶子节点（PL-TAX 等为 level 2，不能仅限 level 3）
      if (children.length === 0 && (code || sc)) {
        if (sc) index[sc] = node;
        if (code) index[code] = node;
      }
      
      for (const child of children) {
        traverse(child, statement);
      }
    };
    
    for (const node of (legacyData.bsLine || [])) {
      traverse(node, 'BS');
    }
    for (const node of (legacyData.plLine || [])) {
      traverse(node, 'PL');
    }

    set({
      adjustRecord: data,
      byCode: index,
      perDataByCode: perDataIndex,
    });
  },

  setRoundingGroup: (group) => {
    set({ roundingGroup: group });
  },

  syncDraftFinalAmount: async (yearOfAssessment?: string | null) => {
    const record = get().adjustRecord;
    if (!record) {
      console.warn('[fsAdjustStore] No adjustRecord to save draft');
      return false;
    }

    const companyId = Number(record.companyId);
    const annualId = Number(record.annualId);
    if (!companyId || !annualId) {
      console.warn('[fsAdjustStore] Missing companyId or annualId');
      return false;
    }

    const yoaKey = String(yearOfAssessment || '').trim();
    const gateKey = `${companyId}:${annualId}:${yoaKey}`;
    const existing = draftFinalAmountInFlight.get(gateKey);
    if (existing) {
      return existing;
    }

    const pending = (async (): Promise<boolean> => {
      // 让出主线程，避免 buildComputedDraftNodes / 大 payload 序列化卡住首屏渲染
      await new Promise<void>((resolve) => setTimeout(resolve, 0));

      const { bsNodes, plNodes } = buildComputedDraftNodes(
        get().adjustRecord!,
        get().roundingGroup,
        yearOfAssessment,
      );

      try {
        await draftFinalAmount({
          companyId,
          annualId,
          bsNodes,
          plNodes,
        });
        return true;
      } catch (err) {
        console.error('[fsAdjustStore] Failed to save draft final amount to Redis:', err);
        return false;
      } finally {
        draftFinalAmountInFlight.delete(gateKey);
      }
    })();

    draftFinalAmountInFlight.set(gateKey, pending);
    return pending;
  },

  getLineByCode: (code) => {
    const key = String(code || '').trim();
    if (!key) return null;
    return get().byCode[key] ?? null;
  },

  getPerData: (code: string, yearOfAssessment?: string | null) => {
    const key = String(code || '').trim().toUpperCase();
    if (!key) return null;
    const map = get().perDataByCode;
    // Omit / empty YOA → SINGLE path (CODE only). Non-empty YOA → MULTI CODE@YOA then CODE fallback.
    const yoa = String(yearOfAssessment || '').trim();
    if (!yoa) return map[key] ?? null;
    return map[`${key}@${yoa}`] ?? map[key] ?? null;
  },

  /**
   * 获取 Balance Sheet 树
   * @returns BS 树数组或 null
   */
  getBsTree: () => {
    const record = get().adjustRecord;
    
    if (!record || !record.bsLine) {
      return null;
    }
    
    // API already returns tree structure, just convert the format
    const result = (record.bsLine || []).map(convertAuditFsItemVOToFsSimpleData);
    return result;
  },

  /**
   * 获取 Profit Loss 树
   * @returns PL 树数组或 null
   */
  getPlTree: () => {
    const record = get().adjustRecord;
    
    if (!record || !record.plLine) {
      return null;
    }
    
    // API already returns tree structure, just convert the format
    const result = (record.plLine || []).map(convertAuditFsItemVOToFsSimpleData);
    return result;
  },

  /**
   * 获取 paper 详情
   * @param companyId 公司 ID
   * @param annualId 年度 ID
   * @param code schedule code
   * @returns paper 详情数组
   */
  getWpDetail: async (companyId, annualId, code, options) => {
    const apiCode = resolveWpFetchCode(code);
    const yoa = String(options?.yearOfAssessment || '').trim();
    // MULTI toPaper assemble: cache per YOA so Drawer-picked year is not poisoned by flat SINGLE fetch
    const cacheKey = yoa ? `${code}@${yoa}` : code;
    const apiCacheKey = yoa ? `${apiCode}@${yoa}` : apiCode;
    const cached = get().wpDetailsCache[cacheKey] ?? get().wpDetailsCache[apiCacheKey];

    if (cached) {
      return cached;
    }

    try {
      const params: WorkingPaperQueryRequest = {
        companyId,
        annualId,
        code: apiCode,
        ...(yoa ? {yearOfAssessment: yoa} : {}),
      };
      const result = await getWorkingPaper(params);

      set((state: FsAdjustState) => ({
        wpDetailsCache: {
          ...state.wpDetailsCache,
          [cacheKey]: result,
          ...(apiCacheKey !== cacheKey ? {[apiCacheKey]: result} : {}),
        },
      }));

      return result;
    } catch (e: any) {
      console.error('Failed to get paper detail:', e);
      throw e;
    }
  },


  setComputedPerData: (code: string, data: FsPerData, yearOfAssessment?: string | null) => {
    const key = String(code || '').trim().toUpperCase();
    if (!key) return;
    const yoa = String(yearOfAssessment || '').trim();
    // MULTI Period Tab: store under CODE@YOA so mounted tabs do not last-writer-wins.
    // SINGLE (no YOA): keep writing CODE only (unchanged).
    const storeKey = yoa ? `${key}@${yoa}` : key;
    set((state) => ({
      perDataByCode: {
        ...state.perDataByCode,
        [storeKey]: data,
      },
    }));
  },

  /**
   * 获取与 draftFinalAmount 请求一致的计算后 FsNodeDraft 数据
   * 包含所有 COT/BS/PL 派生节点的覆盖计算
   * MULTI 时可传入 yearOfAssessment，按该 YOA 的 plLine 构建 PL draft
   * @returns { bsNodes, plNodes } 或 null
   */
  getComputedDraftNodes: (yearOfAssessment?: string | null) => {
    const record = get().adjustRecord;
    if (!record) return null;
    return buildComputedDraftNodes(record, get().roundingGroup, yearOfAssessment);
  },

  /**
   * 将当前 store 中的 audited 数据保存到后端 Redis 缓存
   * 用于 FsAdjustment 组件初始化计算完成后调用
   * @returns 是否保存成功
   */
  saveDraftToBackend: async () => {
    return get().syncDraftFinalAmount();
  },

  patchItemAlias: (code, patch) => {
    const key = String(code || '').trim().toUpperCase();
    if (!key) return;

    const namePatch =
      patch.name !== undefined ? {name: patch.name ?? ''} : {};
    const aliasPatch = {
      ...(patch.aliasEn !== undefined ? {aliasEn: patch.aliasEn} : {}),
      ...(patch.aliasZh !== undefined ? {aliasZh: patch.aliasZh} : {}),
    };

    const patchNode = (node: AuditAdjust): AuditAdjust => {
      const nodeCode = String(node.code || '').trim().toUpperCase();
      let next = node;
      if (nodeCode === key) {
        next = {
          ...node,
          ...namePatch,
          ...aliasPatch,
        };
      }
      if (Array.isArray(node.children) && node.children.length > 0) {
        const children = node.children.map(patchNode);
        if (children !== node.children || next !== node) {
          next = {...next, children};
        }
      }
      return next;
    };

    set((state) => {
      const record = state.adjustRecord;
      if (!record) return state;
      const prevLine = state.byCode[key];
      const byCode = prevLine
        ? {
            ...state.byCode,
            [key]: {
              ...prevLine,
              ...namePatch,
              ...aliasPatch,
            },
          }
        : state.byCode;
      return {
        byCode,
        adjustRecord: {
          ...record,
          bsLine: Array.isArray(record.bsLine) ? record.bsLine.map(patchNode) : record.bsLine,
          plLine: Array.isArray(record.plLine) ? record.plLine.map(patchNode) : record.plLine,
        },
      };
    });
  },

}));
